import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  capitalizeFirstLetter,
  fullNameChild,
  handleSubmitCourse,
  // handleDeclareCourse,
} from "../../../Utils/Function";
import Functions from "../../../Helpers/Functions";
import {
  DataProposal,
  DataPunctual,
} from "../../../Objects/interfaces/AppInterface";
import useTailwindBrand from "../../../Theme/useTailwindBrand";
import { BrandContent } from "../../../Components/shared";
import { ENV_TEACHR, ENV_VERIFIED_TRAINER, getActiveClientBrandConfig, getActivePrestationBrandConfig, isBrandActive, isDepositEnabled } from "../../../config/environment.config";
import IndexStep from "../IndexStep";
import { useDeclarationContexte } from "../../../Contexts/DeclarationContexte";
import { useTravelScenario } from "../Travel/TravelModeSelector";
import TravelStore from "../Travel/TravelStore";
import { isCooperativeCoach, isVerifiedTrainer, vtCoachShareTtc, vtCoachVat, vtTerritorialVatRate, vtTtcFromHourlyPaid } from "../../../Utils/VerifiedTrainerAmounts";
import { coachIsSubjectToVat, coachVatIncluded } from "../../../Utils/CoachVatAmounts";
import ComplianceAlert from "../Travel/ComplianceAlert";
import TravelRepository, { ComplianceAlertInfo } from "../../../Repositories/TravelRepository";
import { SessionRow, sessionStart, sessionsToApiPayload, sortSessionRows } from "../SessionList";

/**
 * Texte long du récapitulatif : replié par défaut (aperçu de 2 lignes),
 * un clic sur l'en-tête le déroule. Le lien « Modifier » renvoie à l'étape concernée.
 */
const CollapsibleText = ({ label, text, onEdit, linkClassName }: {
  label: React.ReactNode;
  text: string;
  onEdit: () => void;
  linkClassName: string;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left font-semibold bg-gray-50"
      >
        <span>{label}</span>
        <span className="flex items-center gap-1 text-xs font-normal text-gray-500 whitespace-nowrap">
          {open ? "Masquer" : "Voir"}
          <span
            aria-hidden
            className="inline-block transition-transform duration-200"
            style={{ transform: open ? "rotate(180deg)" : "none" }}
          >
            ▾
          </span>
        </span>
      </button>
      <div className="px-3 py-2">
        <p
          className="font-normal whitespace-pre-line break-words m-0 md:text-base text-sm"
          style={open ? undefined : {
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {text}
        </p>
        {open && (
          <div className="mt-2">
            <span onClick={onEdit} className={`text-sm font-normal underline cursor-pointer ${linkClassName}`}>
              Modifier
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

const LastStep = () => {
  const { handleChangeStep, generalStep, teachr } = useDeclarationContexte();
  const punctualCourse = useSelector((state: any) => state.endDeclareCourse);
  const dispatch = useDispatch();
  const [load, setLoad] = useState(false);
  const { classes, common } = useTailwindBrand()
  const { checkTravelScenario, travelModals, travelLoading } = useTravelScenario(teachr);
  // Verified Trainer : prix total TTC du cours affiché avant le prix payé par le client et la rémunération
  const isVt = isVerifiedTrainer()
  // P7 : anomalie mode de déplacement / distance, affichée dans le récapitulatif
  // sans jamais conditionner la déclaration
  const complianceClientId: number | undefined = generalStep?.firstStep?.child?.clientId;
  const [complianceAlert, setComplianceAlert] = useState<ComplianceAlertInfo | null>(
    TravelStore.getInfo(complianceClientId)?.complianceAlert ?? null
  );
  useEffect(() => {
    if (!teachr?.id || !complianceClientId) return;
    let cancelled = false;
    TravelRepository.getTravelInfo(teachr.id, complianceClientId)
      .then((info) => {
        if (!cancelled) setComplianceAlert(info.complianceAlert ?? null);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [teachr?.id, complianceClientId]);
  const dataCourse = {
    cours: generalStep?.cours,
    step: generalStep?.step,
    child: generalStep?.firstStep?.child,
    duration: generalStep?.cours
      ? generalStep?.secondStep?.durationMore
      : generalStep?.secondStep?.duration,
    startDatetime: generalStep?.cours
      ? generalStep?.secondStep?.startDatetimeMore
      : generalStep?.secondStep?.startDatetime,
    endDatetime: generalStep?.cours
      ? generalStep?.secondStep?.endDatetimeMore
      : generalStep?.secondStep?.endDatetime,
    price: generalStep?.thirdStep?.price,
    subject: isBrandActive(ENV_VERIFIED_TRAINER) ? generalStep?.thirdStep?.subject : generalStep?.thirdStep?.subject[0],
    teachrPrice: generalStep?.thirdStep?.teachrPrice,
    notes: generalStep?.secondStep?.note,
    // déroulement propre à ce cours (étape 1), null = description habituelle
    sessionDescriptionOverride: generalStep?.firstStep?.sessionDescriptionOverride ?? null,
    sessions: generalStep?.secondStep?.sessions,
    deposit: generalStep?.thirdStep?.deposit,
    depositDate: generalStep?.thirdStep?.depositDate,
    depositStay: generalStep?.thirdStep?.depositStay,
    proofDeposit: generalStep?.thirdStep?.proofDeposit,
    proofDeposits: generalStep?.thirdStep?.proofDeposits,
    depositAttestationAccepted: generalStep?.thirdStep?.depositAttestationAccepted,
    isOver: generalStep?.secondStep?.isOver
  };
  // Part du prof comme sur sa facture. Coopérative Verified Trainer SAP (marque Verified Trainer, profs Teach'r en
  // coopérative) : part HT du cours, TVA ajoutée par-dessus s'il est assujetti, au taux du territoire du client
  // (code postal de son adresse par défaut). Teach'r et Déclarons : prix horaire du prof × heures, TVA comprise.
  const coopSplit = isCooperativeCoach(teachr);
  const territorialRate = vtTerritorialVatRate(dataCourse.child?.zip);
  const coachTtc = coopSplit
    ? vtCoachShareTtc(vtTtcFromHourlyPaid(dataCourse.price, dataCourse.duration), teachr, territorialRate)
    : (parseFloat(dataCourse.teachrPrice) || 0) * (parseFloat(dataCourse.duration) || 0);
  const coachAssujetti = coachIsSubjectToVat(teachr);
  const coachTva = coopSplit
    ? vtCoachVat(vtTtcFromHourlyPaid(dataCourse.price, dataCourse.duration), teachr, territorialRate)
    : coachVatIncluded(coachTtc, teachr);

  const course = {
    forChild_id: dataCourse?.child?.childId,
    chapter: dataCourse?.isOver ? "over" : "",
    address_id: dataCourse?.child?.addressId,
    subjectAsked_id: isBrandActive(ENV_VERIFIED_TRAINER) ? dataCourse?.subject : dataCourse?.subject?.id,
    startDateTime: dataCourse?.startDatetime,
    endDateTime: dataCourse?.endDatetime,
    isRemote: false,
    teachr_id: teachr?.id,
    teachrId: teachr["@id"],
    isCourseInTheHour: false,
    duration: (dataCourse?.duration * 2).toString(),
    isDirectCourse: true,
    isFiscalReductionEnabled: false,
    paymentMean: "",
    aiciActivated: true,
    chapterExtraInformation: "",
    globalExtraInformation: dataCourse?.notes ? dataCourse?.notes : "",
    sessionDescriptionOverride: dataCourse?.sessionDescriptionOverride ?? null,
    isPunctualMonth: dataCourse?.cours,
    isPassedCourse: false,
    // P1 – séances détaillées du cours mensuel envoyées au backend
    sessions: dataCourse?.cours && Array.isArray(dataCourse?.sessions)
      ? sessionsToApiPayload(dataCourse.sessions)
      : null,
    description: null,
    teachrPrice: dataCourse?.teachrPrice,
    deposit: parseFloat(dataCourse?.deposit),
    depositStay: parseFloat(dataCourse?.depositStay),
    depositDate: moment(dataCourse?.depositDate).toDate(),
    proofDeposit: dataCourse?.cours ? null : dataCourse?.proofDeposit?.id,
    proofDeposits: dataCourse?.cours && Array.isArray(dataCourse?.proofDeposits)
      ? dataCourse.proofDeposits.map((p: any) => p?.id).filter(Boolean)
      : null,
    // attestation sur l'honneur d'acompte certifiée à l'étape 3
    depositAttestationAccepted: dataCourse?.depositAttestationAccepted === true,

  };

  const data: DataProposal = {
    value: 5,
    isCoursePass: false,
    setLoad: setLoad,
    teachr: teachr,
    clientId: dataCourse?.child?.clientId,
    isCours: dataCourse?.cours,
    handleChangeStep: handleChangeStep,
    dispatch: dispatch,
    punctualCourseId: punctualCourse?.punctualCourse ? punctualCourse?.punctualCourse : null,
    courseProposalId: punctualCourse?.courseProposalId ? punctualCourse?.courseProposalId : null,
  };
  const dataPunctual: DataPunctual = {
    course,
    data: data,
  };

  const displayBody = () => {
    return (
      <BrandContent
        tag="div"
        children={<>
          <BrandContent
            tag="p"
            children={<>
              Avant de valider votre déclaration, assurez-vous de passer en revue
              chaque information avec attention pour éviter toute erreur.
            </>}
            type="description"
            className={`mb-`}
          />

          <div className="list-disc px-4">
            <li className={`font-semibold ${isBrandActive(ENV_VERIFIED_TRAINER) ? "hidden" : ""}`}>
              {isBrandActive(ENV_TEACHR) ? "Matière" : "Prestation"} :{" "}
              <span
                onClick={() => handleChangeStep(3)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}
              >
                {dataCourse?.subject?.fr_FR}
              </span>
            </li>
            <li className="font-semibold /text-grey">
              Durée :{" "}
              <span
                onClick={() => handleChangeStep(2)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}
              >
                {Functions.renderDuration(dataCourse.duration * 2)}
              </span>
            </li>
            <li className="font-semibold /text-grey">
              Type de {getActivePrestationBrandConfig()} :{" "}
              <span
                onClick={() => handleChangeStep(1)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}
              >
                {dataCourse.cours ? "Plusieurs cours" : "Ponctuel"}
              </span>{" "}
            </li>
            {/* <ToolTip
            text={
              dayCondition &&
              (hoursCondition
                ? "L'heure de début du cours que vous avez indiquée est déjà écoulée. Merci de modifier l'heure de début."
                : "")
            }
          > */}
            <li className="font-semibold /text-grey">
              {dataCourse.cours ? "Periode des cours" : "Date et heure"} :{" "}
              <span
                onClick={() => handleChangeStep(2)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}
              >
                {dataCourse.cours && "Du"}{" "}
                {capitalizeFirstLetter(
                  moment(dataCourse?.startDatetime).format(
                    dataCourse.cours ? "ll" : "dddd DD MMMM YYYY"
                  )
                )}{" "}
                {dataCourse.cours ? "au" : "à"}{" "}
                {dataCourse.cours
                  ? capitalizeFirstLetter(
                    moment(dataCourse?.endDatetime).format(
                      dataCourse.cours ? "ll" : "dddd DD MMMM YYYY"
                    )
                  )
                  : Functions.renderTime(dataCourse?.startDatetime)}
              </span>
            </li>
            {/* P1 – détail des séances du cours mensuel */}
            {dataCourse?.cours && Array.isArray(dataCourse?.sessions) && dataCourse.sessions.length > 0 && (
              <li className="font-semibold /text-grey">
                Séances déclarées ({dataCourse.sessions.length}) :{" "}
                <span
                  onClick={() => handleChangeStep(2)}
                  className={`font-normal cursor-pointer`}
                >
                  <ul className="list-none pl-4 space-y-0.5">
                    {/* ordre chronologique, quel que soit l'ordre de saisie */}
                    {sortSessionRows(dataCourse.sessions as SessionRow[]).map((session, index) => {
                      const start = sessionStart(session);
                      return (
                        <li key={session.key ?? index} className={`underline ${classes.primaryText}`}>
                          {start
                            ? `${capitalizeFirstLetter(start.format("dddd DD MMMM YYYY"))} à ${start.format("HH:mm")} - ${Functions.renderDuration(session.duration * 2)}`
                            : `Séance n°${index + 1}`}
                        </li>
                      );
                    })}
                  </ul>
                </span>
              </li>
            )}
            {/* </ToolTip> */}
            <li className="font-semibold /text-grey">
              {capitalizeFirstLetter(getActiveClientBrandConfig())} :{" "}
              <span
                onClick={() => handleChangeStep(1)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}
              >
                {fullNameChild(
                  dataCourse?.child?.childFirstName,
                  dataCourse.child?.childLastName
                )}
                {/* {fullNameChild(child?.childFirstName, child?.childLastName?.toUpperCase())} */}
              </span>
            </li>
            {dataCourse?.child?.isTeachrContact && isDepositEnabled() ? (
              dataCourse?.deposit ? (
                <>
                  <li className="font-semibold /text-grey">
                    Montant déjà réglé par votre {getActiveClientBrandConfig()} :{" "}
                    <span
                      onClick={() => handleChangeStep(3)}
                      className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                    >
                      {parseFloat(dataCourse.deposit).toFixed(2)} €
                    </span>
                  </li>
                  <li className="font-semibold /text-grey">
                    Reste à charge pour votre {getActiveClientBrandConfig()} :{" "}
                    <span
                      onClick={() => handleChangeStep(3)}
                      className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                    >
                      {dataCourse.depositStay ? parseFloat(dataCourse.depositStay).toFixed(2) : 0}€
                    </span>
                  </li>
                  <li className="font-semibold /text-grey">
                    Attestation sur l'honneur d'acompte :{" "}
                    <span
                      onClick={() => handleChangeStep(3)}
                      className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                    >
                      {dataCourse?.depositAttestationAccepted ? "certifiée, signée électroniquement à la validation" : "à certifier à l'étape 3"}
                    </span>
                  </li>
                  {dataCourse?.cours
                    ? (Array.isArray(dataCourse?.proofDeposits) && dataCourse.proofDeposits.length > 0 &&
                      <li className="font-semibold /text-grey">
                        Preuves d'acompte :{" "}
                        <span className="font-normal inline-flex flex-wrap gap-x-3">
                          {dataCourse.proofDeposits.map((proof: any, index: number) => (
                            <a
                              key={proof?.id ?? index}
                              target="_blank"
                              rel="noreferrer"
                              download
                              href={proof?.contentUrl}
                              className={`underline ${classes.primaryText}`}
                            >
                              {proof?.filePath || `Preuve ${index + 1}`}
                            </a>
                          ))}
                        </span>
                      </li>
                    )
                    : (dataCourse?.proofDeposit &&
                      <li className="font-semibold /text-grey">
                        Preuve d'acompte :{" "}
                        <span
                          onClick={() => handleChangeStep(3)}
                          className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                        >
                          <a target="_blank" rel="noreferrer" download href={dataCourse?.proofDeposit?.contentUrl} className=''>{dataCourse?.proofDeposit?.filePath}</a>

                        </span>
                      </li>
                    )
                  }
                  {dataCourse?.depositDate &&
                    <li className="font-semibold /text-grey">
                      Date d'acompte :{" "}
                      <span
                        onClick={() => handleChangeStep(3)}
                        className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                      >
                        {capitalizeFirstLetter(
                          moment(dataCourse?.depositDate).format(
                            dataCourse.cours ? "ll" : "dddd DD MMMM YYYY"
                          )
                        )}
                      </span>
                    </li>
                  }
                </>
              ) :
                <>
                  {isVt && (
                    <li className="font-semibold /text-grey">
                      Prix total {dataCourse?.cours ? "pour les cours" : "par séance"} :{" "}
                      <span
                        onClick={() => handleChangeStep(3)}
                        className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                      >
                        {vtTtcFromHourlyPaid(dataCourse.price, dataCourse.duration).toFixed(2)} €
                      </span>
                    </li>
                  )}
                  <li className="font-semibold /text-grey">
                    Prix payé {dataCourse?.cours ? "pour les cours" : "par séance"} :{" "}
                    <span
                      onClick={() => handleChangeStep(3)}
                      className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                    >
                      {(dataCourse.price * dataCourse.duration).toFixed(2)} €
                    </span>
                  </li>
                </>
            ) : isVt ? (
              // Verified Trainer (pas d'acompte) : prix total TTC du cours, puis ce que paie le client (la moitié)
              <>
                <li className="font-semibold /text-grey">
                  Prix total {dataCourse?.cours ? "pour les cours" : "par séance"} :{" "}
                  <span
                    onClick={() => handleChangeStep(3)}
                    className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                  >
                    {vtTtcFromHourlyPaid(dataCourse.price, dataCourse.duration).toFixed(2)} €
                  </span>
                </li>
                <li className="font-semibold /text-grey">
                  Prix payé par votre {getActiveClientBrandConfig()} {dataCourse?.cours ? "pour les cours" : "par séance"} :{" "}
                  <span
                    onClick={() => handleChangeStep(3)}
                    className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                  >
                    {((parseFloat(dataCourse.price) || 0) * (parseFloat(dataCourse.duration) || 0)).toFixed(2)} €
                  </span>
                </li>
              </>
            ) : (
              <li className="font-semibold /text-grey">
                Rémunération horaire :{" "}
                <span
                  onClick={() => handleChangeStep(3)}
                  className={`font-normal underline cursor-pointer ${classes.primaryText}`}
                >
                  {parseFloat(dataCourse.price).toFixed(2)} €
                </span>
              </li>
            )}

            <li className="font-semibold /text-grey">
              Rémunération totale{coachAssujetti ? " TTC" : ""}{" "}
              {dataCourse?.cours ? "pour les cours" : "par séance"} :{" "}
              <span onClick={() => handleChangeStep(3)}
                className={`font-normal underline cursor-pointer ${classes.primaryText}`}>
                {coachTtc.toFixed(2)} €
                {coachAssujetti ? ` (dont ${coachTva.toFixed(2)} € de TVA)` : ""}
              </span>
            </li>
            {/* seule une description personnalisée est rappelée : la description
                habituelle est renseignée une fois pour toutes dans les paramètres */}
            {dataCourse?.sessionDescriptionOverride && (
              <li className="font-semibold">
                Déroulement personnalisé du {getActivePrestationBrandConfig()} :{" "}
                <CollapsibleText
                  label=""
                  text={dataCourse.sessionDescriptionOverride}
                  onEdit={() => handleChangeStep(1)}
                  linkClassName={classes.primaryText}
                />
              </li>
            )}
            {dataCourse?.cours &&
              <li className="font-semibold /text-grey">
                Note optionnelle à destination de votre {getActiveClientBrandConfig()} :{" "}
                {dataCourse?.notes && (
                  <li className="list-none">
                    <CollapsibleText
                      label={""}
                      text={dataCourse.notes}
                      onEdit={() => handleChangeStep(2)}
                      linkClassName={classes.primaryText}
                    />
                  </li>
                )}
              </li>
            }

          </div>
          <BrandContent
            tag="p"
            children={<>
              Une fois la demande de paiement transmise à l'Urssaf, votre client
              sera informé par e-mail. Il disposera alors de 48 heures pour valider
              ou contester le paiement. Si aucune action n’est effectuée dans ce
              délai, le paiement sera automatiquement validé.
            </>}
            type="description"
            className={`mb-2`}
          />
        </>}
        type="description"
        className={`space-y-4 ${common.body}`}
      />
    );
  };

  const handleDeclare = () => {
    // P5 : re-vérifie le scénario distance avant de déclarer (attestation ou
    // preuve manquante après un rafraîchissement de page, par exemple).
    // P6 : les dates finales du cours permettent de vérifier que l'abonnement
    // de transports en commun couvre bien toute la période.
    checkTravelScenario(dataCourse?.child, () => {
      dataPunctual.data.travelProofFile = TravelStore.getProofFile(dataCourse?.child?.clientId);
      // P6 : l'abonnement de transports en commun valide sert de preuve
      dataPunctual.data.travelProofFromSubscription = TravelStore.getUseSubscription(dataCourse?.child?.clientId);
      handleSubmitCourse(dataPunctual);
    }, { start: dataCourse?.startDatetime, end: dataCourse?.endDatetime });
  };
  return (
    <>
      {travelModals}
      <IndexStep
        title="Récapitulatif 📋:"
        body={<>
          {complianceAlert && <ComplianceAlert alert={complianceAlert} onConfirmed={setComplianceAlert} className="mb-3" />}
          {displayBody()}
        </>}
        handleChangeStep={handleChangeStep}
        handleNexStep={handleDeclare}
        value={5}
        step={4}
        load={load || travelLoading}
        disabled={load || travelLoading}
        generalStep={generalStep}
      />
    </>
  );
};

export default LastStep;
